<template>
    <el-dialog :title="title || '选择审批人'" :visible.sync="visible" width="560px" :close-on-click-modal="false" @close="onClose">
        <div class="picker-filter">
            <el-select v-model="deptId" placeholder="全部部门" clearable size="small" style="width: 200px">
                <el-option label="全部部门" value="" />
                <el-option v-for="d in departments" :key="d.id" :label="d.name" :value="String(d.id)" />
            </el-select>
            <el-input v-model="keyword" placeholder="搜索姓名 / 账号" clearable size="small" prefix-icon="el-icon-search" style="width: 200px; margin-left: 10px" />
        </div>

        <div class="user-list" v-loading="loading">
            <el-empty v-if="!filteredUsers.length" description="无匹配人员" />
            <!-- 按步骤分别选人模式 -->
            <div v-else-if="steps.length" class="step-mode">
                <div v-for="(step, si) in steps" :key="step" class="step-block">
                    <div class="step-title">{{ si + 1 }}. {{ step }}
                        <span v-if="isStepLocked(step)" class="lock-badge">（固定终审人）</span>
                        <span v-else class="step-count">（已选 {{ (stepSelections[step] || []).length }} 人）</span>
                    </div>
                    <div v-if="isStepLocked(step)" class="locked-users">
                        <el-select :value="(stepSelections[step] || [])[0]" disabled size="small" style="width: 280px">
                            <el-option v-for="id in (stepSelections[step] || [])" :key="id" :label="userName(id)" :value="id" />
                        </el-select>
                        <span class="lock-hint">固定终审人，不可修改</span>
                    </div>
                    <el-checkbox-group v-else v-model="stepSelections[step]" class="user-group">
                        <div v-for="u in filteredUsers" :key="u.id" class="user-item">
                            <el-checkbox :label="u.id">
                                <span class="user-name">{{ u.name || u.username }}</span>
                                <span class="user-dept">{{ deptName(u.departmentId) }}</span>
                                <span class="user-account">{{ u.username }}</span>
                            </el-checkbox>
                        </div>
                    </el-checkbox-group>
                </div>
            </div>
            <!-- 传统单一审批人列表模式 -->
            <el-checkbox-group v-else v-model="selectedIds" class="user-group">
                <div v-for="u in filteredUsers" :key="u.id" class="user-item">
                    <el-checkbox :label="u.id">
                        <span class="user-name">{{ u.name || u.username }}</span>
                        <span class="user-dept">{{ deptName(u.departmentId) }}</span>
                        <span class="user-account">{{ u.username }}</span>
                    </el-checkbox>
                </div>
            </el-checkbox-group>
        </div>

        <div class="selected-summary">
            <template v-if="steps.length">
                <div v-for="(step, si) in steps" :key="step" class="step-summary">
                    <span>{{ si + 1 }}. {{ step }}：</span>
                    <template v-if="!(stepSelections[step] || []).length"><span class="no-selected">未选</span></template>
                    <el-tag v-for="id in (stepSelections[step] || [])" :key="id" size="mini"
                            :closable="!isStepLocked(step)" @close="removeStepSelected(step, id)" class="selected-tag">
                        {{ userName(id) }}
                    </el-tag>
                </div>
            </template>
            <template v-else>
                <span>已选 {{ selectedIds.length }} 人：</span>
                <template v-if="!selectedIds.length"><span class="no-selected">无</span></template>
                <el-tag v-for="id in selectedIds" :key="id" size="mini" closable @close="removeSelected(id)" class="selected-tag">
                    {{ userName(id) }}
                </el-tag>
            </template>
            <div v-if="savedDefaultActive" class="default-hint">已记住默认审批人，确定后自动沿用，可在左下角「清空默认」重置</div>
        </div>

        <span slot="footer">
            <el-button @click="clearDefault" size="small">清空默认</el-button>
            <el-button @click="cancel" size="small">取消</el-button>
            <el-button type="primary" @click="confirm" :disabled="!selectedIds.length" size="small">确定</el-button>
        </span>
    </el-dialog>
</template>

<script>
    export default {
        name: 'UserPicker',
        data() {
            return {
                visible: false,
                title: '选择审批人',
                departments: [],
                users: [],
                deptId: '',
                keyword: '',
                selectedIds: [],
                steps: [],
                stepSelections: {},
                lockedSteps: {},
                excludeUserIds: [],
                autoSelectCount: 3,
                defaultKey: 'approver_default',
                savedDefaultActive: false,
                loading: false,
                resolve: null,
                reject: null
            }
        },
        computed: {
            filteredUsers() {
                const key = (this.keyword || '').trim().toLowerCase()
                return this.users.filter(u => {
                    if (this.excludeUserIds.includes(u.id) || this.excludeUserIds.includes(Number(u.id)) || this.excludeUserIds.includes(String(u.id))) return false
                    if (this.deptId !== '' && String(u.departmentId) !== this.deptId) return false
                    if (!key) return true
                    const name = (u.name || '').toLowerCase()
                    const account = (u.username || '').toLowerCase()
                    return name.includes(key) || account.includes(key)
                })
            }
        },
        methods: {
            open(options = {}) {
                this.visible = true
                this.title = options.title || '选择审批人'
                this.excludeUserIds = Array.isArray(options.excludeUserIds) ? options.excludeUserIds : []
                this.autoSelectCount = (typeof options.autoSelectCount === 'number') ? options.autoSelectCount : 3
                this.defaultKey = options.defaultKey || 'approver_default'
                this.deptId = options.defaultDeptId !== undefined ? String(options.defaultDeptId) : this.currentUserDept()
                this.keyword = ''
                // 按步骤分别选人模式：steps 为非空数组时启用
                if (Array.isArray(options.steps) && options.steps.length) {
                    this.steps = options.steps
                    this.stepSelections = {}
                    // 锁定步骤（如固定终审人）：预置且不可修改
                    this.lockedSteps = (options.lockedSteps && typeof options.lockedSteps === 'object') ? options.lockedSteps : {}
                    options.steps.forEach(s => {
                        const locked = this.lockedSteps[s]
                        this.$set(this.stepSelections, s, (locked ? [...locked] : []))
                    })
                    // 尝试沿用按步骤记忆的默认（锁定步骤不覆盖，保持固定终审人）
                    const saved = this.loadSavedDefaultSteps()
                    if (saved && saved.length === this.steps.length) {
                        this.steps.forEach((s, i) => {
                            if (this.isStepLocked(s)) return
                            this.$set(this.stepSelections, s, [...(saved[i] || [])])
                        })
                        this.savedDefaultActive = true
                    } else {
                        this.savedDefaultActive = false
                    }
                    this.selectedIds = []
                } else {
                    this.steps = []
                    this.stepSelections = {}
                    this.lockedSteps = {}
                    this.selectedIds = Array.isArray(options.defaultSelectedIds) ? [...options.defaultSelectedIds] : []
                    this.savedDefaultActive = false
                }
                this.loadData()
                return new Promise((resolve, reject) => {
                    this.resolve = resolve
                    this.reject = reject
                })
            },
            currentUserDept() {
                try {
                    const u = JSON.parse(localStorage.getItem('userInfo') || '{}')
                    return u.department_id ? String(u.department_id) : ''
                } catch (e) { return '' }
            },
            async loadData() {
                this.loading = true
                try {
                    // 🔴 原实现调 /department/departmentAll + /user/userList，两条都是管理员特权路径
                    //   （JwtInterceptor 要求 vp>=3）。低权限账号（部门经理 vp2 / 设计师 vp1 等）在
                    //   「提交审批选人」「请假选审批人」时必 403，axios 拦截器弹红条「权限不足，需要
                    //   管理员层级」，且人员列表为空 ⇒ 表现成「选人弹窗打不开/选不了人」。
                    //   /approval/staffOptions 是非特权只读接口，且已排除供应商账号（role_id=33）。
                    const [userRes, deptRes] = await Promise.all([
                        this.$http.post('/approval/staffOptions'),
                        this.$http.post('/approval/deptOptions')
                    ])
                    const allUsers = (userRes.data && (userRes.data.list || userRes.data.records)) || []
                    this.users = allUsers.filter(u => Number(u.roleId) !== 33).map(u => ({
                        id: u.id,
                        name: u.name,
                        username: u.username || '',
                        departmentId: u.deptId,   // 沿用模板与过滤逻辑既有字段名
                        deptName: u.deptName || ''
                    }))
                    // 部门下拉同样走非特权接口，并过滤掉供应商部门
                    const allDepartments = (deptRes.data && (deptRes.data.list || deptRes.data.records)) || []
                    this.departments = allDepartments.filter(d => String(d.name).trim() !== '供应商')
                    this.$nextTick(() => {
                        // 若指定了默认部门但无数据，退到全部部门
                        if (this.deptId && !this.filteredUsers.length) {
                            this.deptId = ''
                        }
                        if (!this.selectedIds.length) {
                            // 1) 优先沿用「自己设定的默认审批人」（localStorage 记忆）
                            const saved = this.loadSavedDefault()
                            const valid = (saved || []).filter(id => this.users.some(u => String(u.id) === String(id)))
                            if (valid.length) {
                                this.selectedIds = valid
                                this.savedDefaultActive = true
                            } else if (this.autoSelectCount > 0 && this.filteredUsers.length) {
                                // 2) 无记忆时回退为默认勾选前 N 人（申请人已排除）
                                this.selectedIds = this.filteredUsers.slice(0, this.autoSelectCount).map(u => u.id)
                                this.savedDefaultActive = false
                            }
                        }
                    })
                } catch (e) {
                    this.$message.error('加载人员列表失败')
                } finally {
                    this.loading = false
                }
            },
            deptName(deptId) {
                if (!deptId) return '未分配'
                const d = this.departments.find(item => String(item.id) === String(deptId))
                return d ? d.name : ('部门' + deptId)
            },
            userName(userId) {
                const u = this.users.find(item => String(item.id) === String(userId))
                return u ? (u.name || u.username) : userId
            },
            removeSelected(id) {
                this.selectedIds = this.selectedIds.filter(item => String(item) !== String(id))
            },
            removeStepSelected(step, id) {
                const cur = this.stepSelections[step] || []
                this.$set(this.stepSelections, step, cur.filter(item => String(item) !== String(id)))
            },
            isStepLocked(step) {
                return Array.isArray(this.lockedSteps[step]) && this.lockedSteps[step].length > 0
            },
            storageKey() {
                return 'approver_default_' + this.defaultKey
            },
            loadSavedDefault() {
                try {
                    const raw = localStorage.getItem(this.storageKey())
                    if (!raw) return null
                    const arr = JSON.parse(raw)
                    return Array.isArray(arr) ? arr : null
                } catch (e) { return null }
            },
            saveDefault(ids) {
                try { localStorage.setItem(this.storageKey(), JSON.stringify(ids || [])) } catch (e) {}
            },
            storageKeySteps() {
                return 'approver_default_steps_' + this.defaultKey
            },
            loadSavedDefaultSteps() {
                try {
                    const raw = localStorage.getItem(this.storageKeySteps())
                    if (!raw) return null
                    const arr = JSON.parse(raw)
                    return Array.isArray(arr) ? arr : null
                } catch (e) { return null }
            },
            saveDefaultSteps(stepsArr) {
                try { localStorage.setItem(this.storageKeySteps(), JSON.stringify(stepsArr || [])) } catch (e) {}
            },
            clearDefault() {
                try { localStorage.removeItem(this.storageKey()) } catch (e) {}
                try { localStorage.removeItem(this.storageKeySteps()) } catch (e) {}
                this.savedDefaultActive = false
                this.selectedIds = []
                if (this.steps.length) {
                    // 清空时保留锁定步骤的固定终审人
                    this.steps.forEach(s => {
                        const locked = this.lockedSteps[s]
                        this.$set(this.stepSelections, s, locked ? [...locked] : [])
                    })
                }
                this.$message.success('已清空默认审批人')
            },
            confirm() {
                if (this.steps.length) {
                    // 每级审批至少选 1 人，避免「一个人审到底」
                    for (let i = 0; i < this.steps.length; i++) {
                        const sel = this.stepSelections[this.steps[i]] || []
                        if (!sel.length) {
                            this.$message.warning('请为第 ' + (i + 1) + ' 级【' + this.steps[i] + '】至少选择 1 名审批人')
                            return
                        }
                    }
                    const result = this.steps.map(s => [...(this.stepSelections[s] || [])])
                    this.saveDefaultSteps(result)
                    this.savedDefaultActive = false
                    if (this.resolve) this.resolve(result)
                    this.visible = false
                } else {
                    // 记住本次选择，作为下次打开的默认
                    this.saveDefault([...this.selectedIds])
                    if (this.resolve) this.resolve([...this.selectedIds])
                    this.visible = false
                }
            },
            cancel() {
                if (this.reject) this.reject('cancel')
                this.visible = false
            },
            onClose() {
                this.selectedIds = []
                this.resolve = null
                this.reject = null
            }
        }
    }
</script>

<style scoped>
    .picker-filter { margin-bottom: 12px; }
    .step-mode { display: flex; flex-direction: column; gap: 12px; }
    .step-block { border: 1px solid #ebeef5; border-radius: 6px; padding: 8px 10px; background: #fafafa; }
    .step-title { font-weight: 600; color: #409eff; margin-bottom: 6px; }
    .step-count { font-weight: normal; color: #909399; font-size: 12px; }
    .lock-badge { font-weight: normal; color: #e6a23c; font-size: 12px; margin-left: 4px; }
    .locked-users { display: flex; align-items: center; gap: 8px; padding: 4px 0; }
    .lock-hint { color: #909399; font-size: 12px; }
    .step-summary { margin-bottom: 4px; }
    .step-summary .no-selected { color: #909399; margin-left: 4px; }
    .user-list { max-height: 320px; overflow-y: auto; border: 1px solid #ebeef5; border-radius: 4px; padding: 8px; min-height: 120px; }
    .user-group { display: flex; flex-direction: column; gap: 6px; }
    .user-item { padding: 6px 8px; border-radius: 4px; transition: background .2s; }
    .user-item:hover { background: #f5f7fa; }
    .user-name { font-weight: 500; color: #303133; margin-right: 10px; }
    .user-dept { color: #67c23a; font-size: 12px; margin-right: 10px; }
    .user-account { color: #909399; font-size: 12px; }
    .selected-summary { margin-top: 12px; padding-top: 10px; border-top: 1px solid #ebeef5; font-size: 13px; color: #606266; }
    .no-selected { color: #909399; }
    .selected-tag { margin-right: 6px; margin-bottom: 4px; }
    .default-hint { margin-top: 6px; color: #909399; font-size: 12px; }
    >>> .el-checkbox__label { padding-left: 8px; vertical-align: middle; }
</style>
